import React, { useState, useEffect } from 'react';
import { useUserAndGameContext } from '../../contexts/UserAndGameContext';
import { Box, Typography, Snackbar, Alert, Button } from '@mui/material';
import InstagramFeed from '../SocialWidgets/InstagramFeed';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import useNavigateToTop from '../../hooks/general/useNavigateToTop';
import ContentRenderer from './HomePageComponents/ContentRenderer';
import DisplayContent from './HomePageComponents/DisplayContent';
import ResponsiveDisplayGrid from './HomePageComponents/ResponsiveDisplayGrid';

const TemplatedGameHomePage = () => {
    const {
        gameData,
        masterPoolId,
        gameId,
        gameAddress,
        myUserId,
    } = useUserAndGameContext();

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [videoStates, setVideoStates] = useState({});
    const [selectedMobileButton, setSelectedMobileButton] = useState(null);

    const homePageInfo = gameData?.homePageInfo;
    const sections = homePageInfo?.sections;
    const showInstagramFeed = homePageInfo?.showInstagramFeed;
    const instagramFeedPosition = homePageInfo?.instagramFeedPosition;
    const mobileButtons = homePageInfo?.mobileButtons;
    const colorScheme = homePageInfo?.colorScheme;
    const useDefaults = !homePageInfo;

    useEffect(() => {
        if (mobileButtons && mobileButtons.length > 0) {
            setSelectedMobileButton(mobileButtons[0]); // Default to first button
        }
    }, [mobileButtons]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const navigateToTop = useNavigateToTop();

    // Default sections hardcoded as fallback
    const defaultSections = [
        {
            headerText: "HOME PAGE",
            content: [
                {
                    type: "paragraph",
                    text: "Home Page Content Here!",
                    fontWeight: "700"
                }
            ]
        }
    ];

    const sectionsToRender = useDefaults || !sections || sections.length === 0 ? defaultSections : sections;

    const getFilteredSections = () => {
        if (!mobileButtons || mobileButtons.length === 0 || !isMobile) {
            // Show all sections if no mobile buttons or on desktop
            return sectionsToRender;
        }
    
        return sectionsToRender.filter((section, index) => {
            // Always show first section (index 0)
            if (index === 0) return true;
            
            // Show sections without displayOn property
            if (!section.displayOn) return true;
            
            // Show sections that match selected mobile button
            return section.displayOn === selectedMobileButton;
        });
    };

    // Helper function to get colors for a section based on its visible index
    const getSectionColors = (visibleIndex) => {
        if (!colorScheme) {
            // Default colors if no colorScheme is defined
            return {
                backgroundColor: 'transparent',
                textColor: '#373737',
                headerTextColor: '#373737'
            };
        }

        // Alternate between primary and secondary colors based on visible index
        const isEven = visibleIndex % 2 === 0;
        return isEven ? colorScheme.primary : colorScheme.secondary;
    };

    const renderMobileButtons = () => {
        if (!mobileButtons || mobileButtons.length === 0 || !isMobile) {
            return null;
        }
    
        return (
            <Box sx={{ 
                display: 'flex', 
                marginBottom: '32px',
                justifyContent: 'center',
                padding: '0 20px'
            }}>
                {mobileButtons.map((buttonText, index) => {
                    const isFirst = index === 0;
                    const isLast = index === mobileButtons.length - 1;
                    const isOnlyButton = mobileButtons.length === 1;
                    
                    let borderRadius;
                    if (isOnlyButton) {
                        borderRadius = '4px'; // Full radius if only one button
                    } else if (isFirst) {
                        borderRadius = '4px 0 0 4px'; // Left side rounded
                    } else if (isLast) {
                        borderRadius = '0 4px 4px 0'; // Right side rounded
                    } else {
                        borderRadius = '0'; // No radius for middle buttons
                    }
    
                    return (
                        <Button
                            key={index}
                            variant="contained"
                            onClick={() => setSelectedMobileButton(buttonText)}
                            sx={{
                                backgroundColor: selectedMobileButton === buttonText ? '#07356B' : '#D3D3D3',
                                color: selectedMobileButton === buttonText ? '#ffffff' : '#373737',
                                border: 'none',
                                borderRadius: borderRadius,
                                textTransform: 'none',
                                fontWeight: '600',
                                fontSize: '16px',
                                minWidth: '140px',
                                height: '32px',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: selectedMobileButton === buttonText ? '#054080' : '#C0C0C0',
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            {buttonText}
                        </Button>
                    );
                })}
            </Box>
        );
    };

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    }

    const handleButtonClick = (page) => {
        console.log('Button clicked', page);
        const url = `/${uriEncoded(gameAddress)}?mId=${uriEncoded(masterPoolId)}&gId=${uriEncoded(gameId)}&page=${uriEncoded(page)}`;
        console.log('URL:', url);
        navigateToTop(url);
    }

    const handleExternalLink = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    const gameAdministration = gameData?.gameAdministration;
    
    const isUserInGameAdministration = (gameAdministration, myUserId) => {
        if (!myUserId || !gameAdministration) return false;
        return Object.values(gameAdministration || {}).some(array =>
            Array.isArray(array) && array.includes(myUserId)
        );
    };
    
    const userInGameAdministration = isUserInGameAdministration(gameAdministration, myUserId);

    const handleCopyClick = () => {
        let link = `${process.env.REACT_APP_CLIENT_URL}/${gameAddress}?mId=${masterPoolId}&gId=${gameId}&page=home&invite=true`;

        if (myUserId && !userInGameAdministration) {
            link += `&referralId=${myUserId}`;
        }

        navigator.clipboard.writeText(link).then(() => {
            setSnackbarOpen(true);
        }).catch((err) => {
            console.error('Failed to copy text: ', err);
        });
    };

    const handleSnackbarClose = (event, reason) => {
        setSnackbarOpen(false);
    };

    const handleVideoClick = (videoId) => {
        setVideoStates(prev => ({
            ...prev,
            [videoId]: {
                ...prev[videoId],
                showControls: true,
                isPlaying: true
            }
        }));
    };

    const handleVideoPlay = (videoId) => {
        setVideoStates(prev => ({
            ...prev,
            [videoId]: {
                ...prev[videoId],
                showControls: true,
                isPlaying: true
            }
        }));
    };

    const handleVideoPause = (videoId) => {
        setVideoStates(prev => ({
            ...prev,
            [videoId]: {
                ...prev[videoId],
                isPlaying: false
            }
        }));
    };

    // Simplified renderContentItem function
    const renderContentItem = (item, index, sectionTextColor, nextItem) => {
        return (
            <ContentRenderer 
                item={item}
                index={index}
                sectionTextColor={sectionTextColor}
                nextItem={nextItem}
                isMobile={isMobile}
                videoStates={videoStates}
                handleVideoClick={handleVideoClick}
                handleVideoPlay={handleVideoPlay}
                handleVideoPause={handleVideoPause}
                handleButtonClick={handleButtonClick}
                handleExternalLink={handleExternalLink}
                handleCopyClick={handleCopyClick}
            />
        );
    };

    // Function to render grouped display content
    const renderDisplayContent = (displayContent) => {
        if (!displayContent || !displayContent.items?.length) return null;
        
        return <ResponsiveDisplayGrid displayContent={displayContent} />;
    };

    // UPDATED: renderSection function with alternating colors based on visible index
    const renderSection = (section, visibleIndex) => {
        const {
            headerText,
            content = [],
            displayContent,
            headerFontSize = '24px',
            headerFontWeight = '900',
            headerTextAlign = isMobile ? 'left' : 'center',
            maxWidth = isMobile ? '750px' : '900px',
            width = isMobile ? '90%' : '100%',
            margin = '0 auto',
            marginBottom = '32px',
            sx = {}
        } = section;

        // Get colors based on visible index (alternating)
        const sectionColors = getSectionColors(visibleIndex);
        const { backgroundColor, textColor, headerTextColor } = sectionColors;
        
        const hasBackground = backgroundColor && backgroundColor !== 'transparent';
        const hasVideo = !isMobile && content.some(item => item.type === 'video');
        const padding = hasBackground ? '24px 0' : '0';
    
        if (hasBackground) {
            return (
                <Box
                    key={`section-${visibleIndex}`}
                    className="game-section"
                    sx={{
                        width: '100vw',
                        marginLeft: 'calc(-50vw + 50%)',
                        backgroundColor,
                        marginBottom,
                        padding,
                        ...sx
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: hasVideo ? '1200px' : isMobile ? '750px' : '900px',
                            margin: '0 auto',
                            width: isMobile ? '90%' : '100%',
                            paddingX: isMobile ? '0' : '64px',
                        }}
                    >
                        {hasVideo ? (
                            <Box sx={{ 
                                display: 'flex', 
                                gap: '32px', 
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                margin: '0 auto'
                            }}>
                                <Box sx={{ flex: '0 0 auto' }}>
                                {content?.filter(item => item.type === 'video').map((item, itemIndex) => (
                                    <React.Fragment key={itemIndex}>
                                        {renderContentItem(item, itemIndex, textColor, null)}
                                    </React.Fragment>
                                ))}
                                </Box>
                                
                                <Box sx={{ flex: '0 0 400px', paddingTop: '16px' }}>
                                    {headerText && (
                                        <Typography
                                            className="game-section-header"
                                            sx={{
                                                fontSize: headerFontSize,
                                                color: headerTextColor,
                                                fontWeight: headerFontWeight,
                                                textAlign: headerTextAlign,
                                                marginBottom: '16px'
                                            }}
                                        >
                                            {headerText}
                                        </Typography>
                                    )}

                                    {displayContent?.position === 'before-content' && 
                                        renderDisplayContent(displayContent)
                                    }
    
                                    {content?.map((item, itemIndex) => {
                                        if (item.type === 'video') return null;
                                        
                                        const prevItem = content[itemIndex - 1];
                                        const isSecondButtonInPair = item.type === 'button' && prevItem && prevItem.type === 'button';
                                        
                                        if (isSecondButtonInPair) {
                                            return null;
                                        }

                                        const shouldShowDisplayAfter = 
                                            displayContent?.position === `between-content-${itemIndex + 1}`;
                                        
                                        const nextItem = content[itemIndex + 1];
                                        
                                        return (
                                            <React.Fragment key={itemIndex}>
                                                {renderContentItem(item, itemIndex, textColor, nextItem)}
                                                {shouldShowDisplayAfter && renderDisplayContent(displayContent)}
                                            </React.Fragment>
                                        );
                                    })}

                                    {(!displayContent?.position || displayContent.position === 'after-content') && 
                                        renderDisplayContent(displayContent)
                                    }
                                </Box>
                            </Box>
                        ) : (
                            <>
                                {headerText && (
                                    <Typography
                                        className="game-section-header"
                                        sx={{
                                            fontSize: headerFontSize,
                                            color: headerTextColor,
                                            fontWeight: headerFontWeight,
                                            textAlign: headerTextAlign,
                                            marginBottom: '16px'
                                        }}
                                    >
                                        {headerText}
                                    </Typography>
                                )}

                                {displayContent?.position === 'before-content' && 
                                    renderDisplayContent(displayContent)
                                }
    
                                {content?.map((item, itemIndex) => {
                                    const prevItem = content[itemIndex - 1];
                                    const isSecondButtonInPair = item.type === 'button' && prevItem && prevItem.type === 'button';
                                    
                                    if (isSecondButtonInPair) {
                                        return null;
                                    }

                                    const shouldShowDisplayAfter = 
                                        displayContent?.position === `between-content-${itemIndex + 1}`;
                                    
                                    const nextItem = content[itemIndex + 1];
                                    
                                    return (
                                        <React.Fragment key={itemIndex}>
                                            {renderContentItem(item, itemIndex, textColor, nextItem)}
                                            {shouldShowDisplayAfter && renderDisplayContent(displayContent)}
                                        </React.Fragment>
                                    );
                                })}

                                {(!displayContent?.position || displayContent.position === 'after-content') && 
                                    renderDisplayContent(displayContent)
                                }
                            </>
                        )}
                    </Box>
                </Box>
            );
        }
    
        return (
            <Box
                key={`section-${visibleIndex}`}
                className="game-section"
                sx={{
                    maxWidth: hasVideo ? '1200px' : maxWidth,
                    margin,
                    width,
                    padding,
                    marginBottom,
                    backgroundColor,
                    paddingX: isMobile ? '0' : '64px',
                    ...sx
                }}
            >
                {hasVideo ? (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: '32px', 
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: '0 auto'
                    }}>
                        <Box sx={{ flex: '0 0 auto' }}>
                        {content?.filter(item => item.type === 'video').map((item, itemIndex) => (
                            <React.Fragment key={itemIndex}>
                                {renderContentItem(item, itemIndex, textColor, null)}
                            </React.Fragment>
                        ))}
                        </Box>
                        
                        <Box sx={{ flex: '0 0 400px', paddingTop: '16px' }}>
                            {headerText && (
                                <Typography
                                    className="game-section-header"
                                    sx={{
                                        fontSize: headerFontSize,
                                        color: headerTextColor,
                                        fontWeight: headerFontWeight,
                                        textAlign: headerTextAlign,
                                        marginBottom: '16px'
                                    }}
                                >
                                    {headerText}
                                </Typography>
                            )}

                            {displayContent?.position === 'before-content' && 
                                renderDisplayContent(displayContent)
                            }
    
                            {content?.map((item, itemIndex) => {
                                if (item.type === 'video') return null;
                                
                                const prevItem = content[itemIndex - 1];
                                const isSecondButtonInPair = item.type === 'button' && prevItem && prevItem.type === 'button';
                                
                                if (isSecondButtonInPair) {
                                    return null;
                                }

                                const shouldShowDisplayAfter = 
                                    displayContent?.position === `between-content-${itemIndex + 1}`;
                                
                                const nextItem = content[itemIndex + 1];
                                
                                return (
                                    <React.Fragment key={itemIndex}>
                                        {renderContentItem(item, itemIndex, textColor, nextItem)}
                                        {shouldShowDisplayAfter && renderDisplayContent(displayContent)}
                                    </React.Fragment>
                                );
                            })}

                            {(!displayContent?.position || displayContent.position === 'after-content') && 
                                renderDisplayContent(displayContent)
                            }
                        </Box>
                    </Box>
                ) : (
                    <>
                        {headerText && (
                            <Typography
                                className="game-section-header"
                                sx={{
                                    fontSize: headerFontSize,
                                    color: headerTextColor,
                                    fontWeight: headerFontWeight,
                                    textAlign: headerTextAlign,
                                    marginBottom: '16px'
                                }}
                            >
                                {headerText}
                            </Typography>
                        )}

                        {displayContent?.position === 'before-content' && 
                            renderDisplayContent(displayContent)
                        }
    
                        {content?.map((item, itemIndex) => {
                            const prevItem = content[itemIndex - 1];
                            const isSecondButtonInPair = item.type === 'button' && prevItem && prevItem.type === 'button';
                            
                            if (isSecondButtonInPair) {
                                return null;
                            }

                            const shouldShowDisplayAfter = 
                                displayContent?.position === `between-content-${itemIndex + 1}`;
                            
                            const nextItem = content[itemIndex + 1];
                            
                            return (
                                <React.Fragment key={itemIndex}>
                                    {renderContentItem(item, itemIndex, textColor, nextItem)}
                                    {shouldShowDisplayAfter && renderDisplayContent(displayContent)}
                                </React.Fragment>
                            );
                        })}

                        {(!displayContent?.position || displayContent.position === 'after-content') && 
                            renderDisplayContent(displayContent)
                        }
                    </>
                )}
            </Box>
        );
    };

    const renderInstagramFeed = () => (
        <Box sx={{ marginBottom: '32px' }}>
            <InstagramFeed />
        </Box>
    );

    const filteredSections = getFilteredSections();

    return (
        <Box sx={{ padding: '24px 0 0 0' }}>
            <Box sx={{}}></Box>
            <Box sx={{}}></Box>
            <Box></Box>
    
            {filteredSections?.map((section, visibleIndex) => {
                const shouldShowInstagramAfter = showInstagramFeed && (
                    (instagramFeedPosition === 'after-first' && visibleIndex === 0) ||
                    (instagramFeedPosition === `after-${visibleIndex + 1}`) ||
                    (typeof instagramFeedPosition === 'number' && instagramFeedPosition === visibleIndex + 1)
                );
    
                // Show mobile buttons after first section (index 0) on mobile only
                const shouldShowMobileButtons = visibleIndex === 0 && mobileButtons && mobileButtons.length > 0 && isMobile;
    
                return (
                    <React.Fragment key={visibleIndex}>
                        {renderSection(section, visibleIndex)}
                        {shouldShowMobileButtons && renderMobileButtons()}
                        {shouldShowInstagramAfter && renderInstagramFeed()}
                    </React.Fragment>
                );
            })}
    
            {showInstagramFeed && instagramFeedPosition === 'end' && renderInstagramFeed()}
    
            <Box sx={{
                paddingTop: '32px',
                backgroundImage: 'url(https://poolpartyfilestorage.blob.core.windows.net/media/genericBelt.png)',
                backgroundPosition: '0 50%',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                height: '250px'
            }}>
            </Box>
    
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    Link copied to clipboard!
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default TemplatedGameHomePage;